/**
 * The only place the frontend talks to native code (`04-launcher-and-patcher.md` section 2).
 *
 * Every command is typed here, so a change in the Rust signature shows up as a TypeScript error
 * rather than a runtime surprise. On the web these all reject with `NOT_NATIVE`, which callers
 * never hit because screens check a capability first.
 */

import type { Platform as BuildPlatform } from '@goodbuild/shared';
import { isTauri } from './index.js';

export interface NativeError {
  code: string;
  message: string;
}

export class NativeCallError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'NativeCallError';
  }
}

type InvokeFn = <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
type ListenFn = (event: string, handler: (e: { payload: unknown }) => void) => Promise<() => void>;

let invokeImpl: InvokeFn | null = null;
let listenImpl: ListenFn | null = null;

/** Loads the Tauri API lazily, so the web bundle never pulls it in. */
async function api(): Promise<{ invoke: InvokeFn; listen: ListenFn }> {
  if (!isTauri()) {
    throw new NativeCallError('NOT_NATIVE', 'This only works in the GOOD BUILD app.');
  }
  if (!invokeImpl || !listenImpl) {
    const [core, event] = await Promise.all([
      import('@tauri-apps/api/core'),
      import('@tauri-apps/api/event'),
    ]);
    invokeImpl = core.invoke as InvokeFn;
    listenImpl = event.listen as unknown as ListenFn;
  }
  return { invoke: invokeImpl, listen: listenImpl };
}

async function call<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await api();
  try {
    return await invoke<T>(cmd, args);
  } catch (err) {
    // Rust returns `{ code, message }`; anything else is a bug worth surfacing as it is.
    if (err && typeof err === 'object' && 'code' in err && 'message' in err) {
      const e = err as NativeError;
      throw new NativeCallError(e.code, e.message);
    }
    throw new NativeCallError('NATIVE_ERROR', String(err));
  }
}

/* -------------------------------------------------------------------- auth */

export interface NativeUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: 'member' | 'dev' | 'admin';
}

export const native = {
  authStart: (apiLoginUrl: string) => call<NativeUser>('auth_start', { apiLoginUrl }),
  authGetToken: () => call<string | null>('auth_get_token'),
  authClear: () => call<void>('auth_clear'),

  accessRefresh: () => call<AccessStatus>('access_refresh'),
  accessStatus: () => call<AccessStatus>('access_status'),

  libraryFoldersList: () => call<LibraryFolder[]>('library_folders_list'),
  libraryFoldersAdd: (path: string) => call<LibraryFolder[]>('library_folders_add', { path }),
  libraryFoldersRemove: (path: string) => call<LibraryFolder[]>('library_folders_remove', { path }),

  gamesInstalled: () => call<InstalledGame[]>('games_installed'),
  gameInstall: (gameId: string, slug: string, platform: BuildPlatform, folder?: string) =>
    call<string>('game_install', { gameId, slug, platform, folder }),
  gameUpdate: (gameId: string) => call<string>('game_update', { gameId }),
  gamePause: (gameId: string) => call<void>('game_pause', { gameId }),
  gameResume: (gameId: string) => call<void>('game_resume', { gameId }),
  gameCancel: (gameId: string) => call<void>('game_cancel', { gameId }),
  gameVerify: (gameId: string) => call<string>('game_verify', { gameId }),
  gameUninstall: (gameId: string, deletePrefix = false) =>
    call<void>('game_uninstall', { gameId, deletePrefix }),
  gameLaunch: (gameId: string) => call<void>('game_launch', { gameId }),
  /** Hands the game's install folder to the desktop's file manager. Desktop only. */
  gameReveal: (gameId: string) => call<void>('game_reveal', { gameId }),

  downloadsList: () => call<QueuedJob[]>('downloads_list'),
  downloadsReorder: (jobId: string, delta: number) =>
    call<QueuedJob[]>('downloads_reorder', { jobId, delta }),
  downloadsSetLimit: (bytesPerSecond: number | null) =>
    call<void>('downloads_set_limit', { bytesPerSecond }),
  protonStatus: () => call<ProtonStatus>('proton_status'),

  uploadBuildScan: (path: string, ignoreGlobs: string[]) =>
    call<ScanResult>('upload_build_scan', { path, ignoreGlobs }),
  uploadBuildStart: (input: UploadBuildInput) =>
    call<string>('upload_build_start', { args: input }),
  uploadBuildPause: (jobId: string) => call<void>('upload_build_pause', { jobId }),
  uploadBuildResume: (jobId: string) => call<void>('upload_build_resume', { jobId }),
  uploadBuildCancel: (jobId: string) => call<void>('upload_build_cancel', { jobId }),

  /** Mirrors the tray switch. Resolves false when this desktop has no tray to close into. */
  trayConfigure: (closeToTray: boolean) => call<boolean>('tray_configure', { closeToTray }),

  /**
   * Takes the whole screen for Big Picture, or gives it back.
   *
   * Resolves the state the window is now in, which is false on a build with no window of its
   * own to take: Android is already the whole screen.
   */
  windowSetFullscreen: (fullscreen: boolean) =>
    call<boolean>('window_set_fullscreen', { fullscreen }),

  /* --- the script editor's folders (`03-client-app.md` section 8) --- */

  scriptsWorkspacesList: () => call<ScriptWorkspace[]>('scripts_workspaces_list'),
  scriptsWorkspaceOpen: (path: string) =>
    call<ScriptWorkspace[]>('scripts_workspace_open', { path }),
  scriptsWorkspaceForget: (path: string) =>
    call<ScriptWorkspace[]>('scripts_workspace_forget', { path }),
  scriptsTree: (root: string) => call<ScriptEntry[]>('scripts_tree', { root }),
  scriptsRead: (root: string, path: string) => call<string>('scripts_read', { root, path }),
  /** A content bundle. `.gbo` is binary, so there is no text form to ask for instead. */
  scriptsReadBytes: (root: string, path: string) =>
    call<number[]>('scripts_read_bytes', { root, path }),
  /**
   * A picture a content field points at, looked for under the game's `Content` folder.
   *
   * Not `scriptsReadBytes` with a path worked out on this side, because working it out is the
   * whole job: a field holds `Icons/carrot.png` and the folder that is relative to is the
   * game's, which only the machine with the folder on it can find.
   */
  scriptsContentImage: (root: string, path: string) =>
    call<ContentImage>('scripts_content_image', { root, path }),
  scriptsWrite: (root: string, path: string, text: string) =>
    call<ScriptEntry>('scripts_write', { root, path, text }),
  scriptsWriteBytes: (root: string, path: string, bytes: number[]) =>
    call<ScriptEntry>('scripts_write_bytes', { root, path, bytes }),
  scriptsCreate: (root: string, path: string, text: string) =>
    call<ScriptEntry>('scripts_create', { root, path, text }),
  scriptsCreateFolder: (root: string, path: string) =>
    call<void>('scripts_create_folder', { root, path }),
  scriptsRename: (root: string, from: string, to: string) =>
    call<void>('scripts_rename', { root, from, to }),
  scriptsDelete: (root: string, path: string) => call<void>('scripts_delete', { root, path }),
  scriptsReveal: (root: string, path: string) => call<void>('scripts_reveal', { root, path }),

  deviceInfo: () => call<DeviceInfo>('device_info'),
  notifySchedule: (id: string, atMs: number, title: string, body: string) =>
    call<void>('notify_schedule', { id, atMs, title, body }),
  notifyCancel: (id: string) => call<void>('notify_cancel', { id }),
};

/* ------------------------------------------------------------------ types */

export interface AccessStatus {
  /** A membership, a crew seat or staff: covers every game we publish. */
  hasAccess: boolean;
  /** Games owned outright. Playable whether or not `hasAccess` is true, and after it stops. */
  ownedGameIds: string[];
  accessUntil: number | null;
  lastCheckedAt: number | null;
  /** True when the device clock is behind the last successful check, which voids the pass. */
  clockSuspect: boolean;
}

export interface LibraryFolder {
  path: string;
  freeBytes: number;
  totalBytes: number;
  installedGames: number;
}

export interface InstalledGame {
  gameId: string;
  slug: string;
  buildId: string;
  versionLabel: string;
  platform: BuildPlatform;
  sizeBytes: number;
  folder: string;
  installedAt: number;
}

/**
 * One row of the download queue, as `downloads_list` reports it.
 *
 * The screen opens with these rather than waiting for an event: a queue seen halfway through a
 * 30 GB download has missed every event there was, and a bar that only starts moving once the
 * next chunk lands is a bar that looks broken.
 */
export interface QueuedJob {
  jobId: string;
  gameId: string;
  slug: string;
  kind: JobKind;
  platform: BuildPlatform;
  phase: JobPhase;
  state: JobState;
  paused: boolean;
  /** Place in the queue, zero first. */
  position: number;
  /** True for the one job being worked on. The rest are waiting their turn. */
  running: boolean;
  createdAt: number;
  bytesDone: number;
  bytesTotal: number;
  speedBps: number;
  etaS: number | null;
  /** The size of the build, known before the job has measured anything of its own. */
  buildBytes: number | null;
  error?: NativeError;
}

export interface ProtonStatus {
  installed: boolean;
  protonVersion: string | null;
  umuVersion: string | null;
  downloading: boolean;
}

export interface ScanResult {
  fileCount: number;
  totalBytes: number;
  ignored: string[];
}

export interface UploadBuildInput {
  gameId: string;
  platform: BuildPlatform;
  versionLabel: string;
  path: string;
  publishWhenReady: boolean;
  ignoreGlobs?: string[];
}

/** A folder the script editor has open, as `scripts_workspaces_list` reports it. */
export interface ScriptWorkspace {
  /** The absolute folder. The only absolute path the editor ever handles. */
  path: string;
  /** The folder's own name, which is what the workspace picker shows. */
  label: string;
  openedAt: number;
}

/** A picture a content field points at, found under the game and read. */
export interface ContentImage {
  /** Where it was found, relative to the game folder — not always what was asked for. */
  path: string;
  bytes: number[];
}

/** One file or folder inside a workspace, named relative to its root. */
export interface ScriptEntry {
  path: string;
  directory: boolean;
  bytes: number;
  modifiedAt: number;
}

export interface DeviceInfo {
  platform: 'windows' | 'linux' | 'android';
  isSteamDeck: boolean;
  appVersion: string;
  freeSpaceByFolder: Record<string, number>;
  /** True when the Linux app was started from Desktop Mode rather than Gaming Mode. */
  startedFromDesktopMode: boolean;
}

/* ----------------------------------------------------------------- events */

export type JobKind = 'install' | 'update' | 'verify' | 'uninstall' | 'upload_build';

export type JobPhase =
  | 'queued'
  | 'manifest'
  | 'planning'
  | 'downloading'
  | 'installing'
  | 'verifying'
  | 'committing'
  | 'chunking'
  | 'checking'
  | 'uploading'
  | 'finishing'
  | 'runtime';

export interface JobProgress {
  jobId: string;
  gameId: string;
  kind: JobKind;
  phase: JobPhase;
  bytesDone: number;
  bytesTotal: number;
  speedBps: number;
  etaS: number | null;
  /** Set during an upload so the screen can show "New data: 1.2 GB of 30.4 GB". */
  newBytes?: number;
}

export type JobState =
  | 'queued'
  | 'downloading'
  | 'installing'
  | 'installed'
  | 'paused'
  | 'verifying'
  | 'uninstalling'
  | 'not_installed'
  | 'running'
  | 'error'
  | 'locked';

export interface JobStateEvent {
  jobId: string;
  gameId: string;
  state: JobState;
  error?: NativeError;
}

export interface GameRunningEvent {
  gameId: string;
  pid: number;
}

export interface GameExitedEvent {
  gameId: string;
  durationS: number;
  exitCode: number | null;
}

export interface AccessChangedEvent {
  hasAccess: boolean;
  accessUntil: number | null;
}

export interface GamepadInputEvent {
  button: string;
  pressed: boolean;
  /** True when this press is a repeat of one still being held, rather than a fresh one. */
  repeat?: boolean;
  /** Right stick vertical, used for scrolling the focused area. */
  axis?: number;
  controller: 'xbox' | 'playstation' | 'steamdeck' | 'generic';
}

type EventMap = {
  'job://progress': JobProgress;
  'job://state': JobStateEvent;
  'game://running': GameRunningEvent;
  'game://exited': GameExitedEvent;
  'access://changed': AccessChangedEvent;
  'gamepad://input': GamepadInputEvent;
  /** Where the tray menu asked to go. Typed as the one entry it has, not as any route. */
  'tray://navigate': '/downloads';
};

/** Subscribes to a native event. Returns an unsubscribe function. */
export async function onNative<K extends keyof EventMap>(
  event: K,
  handler: (payload: EventMap[K]) => void,
): Promise<() => void> {
  if (!isTauri()) return () => {};
  const { listen } = await api();
  return listen(event, (e) => handler(e.payload as EventMap[K]));
}
